import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ApplicantDrawer from "@/pages/admin/hiring/ApplicantDrawer";

export default function ApplicantsTab({ meta }) {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [positions, setPositions] = useState([]);
  const [open, setOpen] = useState(params.get("applicant") || "");
  const [filters, setFilters] = useState({
    stage: params.get("stage") || "all", position_id: "all", location: "all", pool: "all",
    q: "", start: "", end: "" });

  const load = useCallback(async () => {
    const query = {};
    if (filters.stage !== "all") query.stage = filters.stage;
    if (filters.position_id !== "all") query.position_id = filters.position_id;
    if (filters.location !== "all") query.location = filters.location;
    if (filters.pool !== "all") query.pool = filters.pool === "yes";
    if (filters.q.trim()) query.q = filters.q.trim();
    if (filters.start) query.start = filters.start;
    if (filters.end) query.end = filters.end;
    try {
      const { data: d } = await api.get("/hiring/applications", { params: query });
      setData(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (meta.can.postings_edit || true) {
      api.get("/hiring/positions").then(({ data: d }) => setPositions(d)).catch(() => setPositions([]));
    }
  }, [meta]);

  const pickStage = (stage) => {
    setFilters({ ...filters, stage });
    setParams(stage === "all" ? {} : { stage });
  };

  return (
    <div data-testid="applicants-tab">
      <div className="flex flex-wrap gap-2" data-testid="stage-counts">
        <button onClick={() => pickStage("all")} data-testid="stage-count-all"
                className={`text-sm font-bold px-4 py-2 rounded-full bg-white border-2 ${filters.stage === "all" ? "border-[#3B82F6]" : "border-border"}`}>
          All · {Object.values(data?.counts || {}).reduce((a, b) => a + b, 0)}
        </button>
        {(data?.stages || meta.stages).map((s) => (
          <button key={s.key} onClick={() => pickStage(s.key)} data-testid={`stage-count-${s.key}`}
                  className={`text-sm font-bold px-4 py-2 rounded-full bg-white border-2 ${filters.stage === s.key ? "border-[#3B82F6]" : "border-border"}`}>
            {s.label} · {data?.counts?.[s.key] ?? 0}
          </button>
        ))}
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 bg-white rounded-2xl border border-border p-4">
        <div className="lg:col-span-2">
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Search</Label>
          <Input className="mt-1.5" placeholder="Name, email, employer, certification" value={filters.q}
                 data-testid="applicant-search" onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Position</Label>
          <Select value={filters.position_id} onValueChange={(v) => setFilters({ ...filters, position_id: v })}>
            <SelectTrigger className="mt-1.5" data-testid="filter-position"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All positions</SelectItem>
              {positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Future pool</Label>
          <Select value={filters.pool} onValueChange={(v) => setFilters({ ...filters, pool: v })}>
            <SelectTrigger className="mt-1.5" data-testid="filter-pool"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              <SelectItem value="yes">Opted in</SelectItem>
              <SelectItem value="no">Not opted in</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Location</Label>
          <Select value={filters.location} onValueChange={(v) => setFilters({ ...filters, location: v })}>
            <SelectTrigger className="mt-1.5" data-testid="filter-location"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {(data?.locations || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Submitted from</Label>
          <Input type="date" className="mt-1.5" value={filters.start} data-testid="filter-start"
                 onChange={(e) => setFilters({ ...filters, start: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">to</Label>
          <Input type="date" className="mt-1.5" value={filters.end} data-testid="filter-end"
                 onChange={(e) => setFilters({ ...filters, end: e.target.value })} />
        </div>
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-border overflow-x-auto" data-testid="applicants-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-xs uppercase tracking-wider text-left">
              {["Applicant", "Position", "Stage", "Submitted", "Reviewer", "Pool", ""].map((h) => (
                <th key={h} className="p-3 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).length === 0 && (
              <tr><td colSpan="7" className="p-6 text-muted-foreground" data-testid="no-applicants">
                No applications match these filters.
              </td></tr>
            )}
            {(data?.rows || []).map((a) => (
              <tr key={a.id} className="border-b border-border last:border-0" data-testid={`applicant-row-${a.id}`}>
                <td className="p-3">
                  <p className="font-bold">{a.name}</p>
                  <p className="text-muted-foreground">{a.email}</p>
                </td>
                <td className="p-3 font-semibold">{a.position_title}</td>
                <td className="p-3 font-semibold">{a.stage_label}</td>
                <td className="p-3">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "Draft"}</td>
                <td className="p-3">{a.reviewer || "—"}</td>
                <td className="p-3">{a.pool_consent?.opt_in ? "Yes" : "No"}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" className="rounded-full font-bold"
                          data-testid={`open-applicant-${a.id}`} onClick={() => setOpen(a.id)}>Open</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <ApplicantDrawer id={open} meta={meta} onClose={() => setOpen("")}
                         onChanged={() => { load(); }} />
      )}
    </div>
  );
}
